import Alert from '@pelckmans/elementis/components/alert';
import Button from '@pelckmans/elementis/components/button';
import Dialog from '@pelckmans/elementis/components/dialog';
import GeneralNotice from '@pelckmans/elementis/components/general-notice';
import keyBy from 'lodash/keyBy';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ANALYTICS_EVENT_SUBLOCATIONS, ANALYTICS_EVENT_TYPES } from '../../../../../../constants/analyticsEvents';
import ModalContext from '../../../../../../contexts/ModalContext';
import useAnalytics from '../../../../../../hooks/useAnalytics';
import LoadingState from '../../../../../../shared-components/LoadingState';
import ModuleSelectionField from '../../../../../../shared-components/ModuleSelectionField';
import ModuleContext from '../../../../_contexts/ModuleContext';
import TableOfContentsContext from '../../../_contexts/TableOfContentsContext';
import { copyMaterial } from './api';
import { NodeWithMaterial, SelectionState } from './components/NodeBranch';
import NodeTree from './components/NodeTree';
import useModulesWithUserMaterial from './hooks/useModulesWithUserMaterial';
import { updateNodeTreeSelection } from './util/selection';
import { UserModule } from '../../../../../my-methods/api/methodsApi';
import { Module } from '../../../../../../api/module';
import SearchField from '@pelckmans/elementis/components/search-field';
import { fuzzyMatch } from '../../../../../../hooks/useFuzzySearch';
import { flattenNodes } from '../../../../../../utils/tocNode';

type Props = {
  id: string;
  onSubmit: () => void;
};

const BASE = 'PAGES.TOC_LEVEL.MODALS.COPY_MATERIAL';
const SUPER_MODULE_PREFS_KEY = 'copyMaterialSuperModulePrefs';
const SUB_MODULE_PREFS_KEY = 'copyMaterialSubModulePrefs';

export default function CopyMaterialModal({ id, onSubmit: onSubmitCallback }: Props) {
  const { t } = useTranslation();
  const { closeModal } = useContext(ModalContext);
  const { selectedNode } = useContext(TableOfContentsContext);
  const { module: currentModule } = useContext(ModuleContext);
  const { capture } = useAnalytics();

  const [selection, setSelection] = useState<Record<string, SelectionState>>({});
  const [showAlert, setShowAlert] = useState<'no-selection' | 'retry' | null>(null);
  const [selectedSuperModule, setSelectedSuperModule] = useState<UserModule | null>(null);
  const [selectedSubModule, setSelectedSubModule] = useState<Module | UserModule | null>(null);
  const {
    modules,
    nodesWithMaterial,
    allNodesWithMaterialById,
    isLoading,
    fetchSubModulesForModule,
    subModulesForModule,
  } = useModulesWithUserMaterial(selectedSubModule);
  const [searchValue, setSearchValue] = useState('');
  const [filteredNodesWithMaterial, setFilteredNodesWithMaterial] = useState<NodeWithMaterial[]>([]);

  const filterNode = (node: NodeWithMaterial, needle: string): NodeWithMaterial | null => {
    const titleMatches = fuzzyMatch(node.title, needle);
    const nodeSelected = Boolean(selection[node.id]);
    const filteredMaterial = titleMatches
      ? node.material
      : node.material?.filter(m => fuzzyMatch(m.name, needle) || Boolean(selection[m.id]));

    const filteredChildren = node.nodes
      ?.map(child => filterNode(child, needle))
      .filter((child): child is NodeWithMaterial & { description: string } => child !== null);

    const materialMatches = (filteredMaterial?.length ?? 0) > 0;
    const childrenMatch = (filteredChildren?.length ?? 0) > 0;

    if (!titleMatches && !materialMatches && !childrenMatch && !nodeSelected) {
      return null;
    }

    return {
      ...node,
      material: titleMatches ? node.material : filteredMaterial,
      nodes: filteredChildren,
    };
  };

  const handleSearch = () => {
    const filtered = nodesWithMaterial
      .map(node => filterNode(node, searchValue))
      .filter((node): node is NodeWithMaterial => node !== null);

    setFilteredNodesWithMaterial(filtered);
  };

  useEffect(() => {
    if (searchValue) {
      handleSearch();
    } else {
      setFilteredNodesWithMaterial(nodesWithMaterial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodesWithMaterial, selectedSuperModule, selectedSubModule]);

  async function selectSuperModule(module: UserModule, preferredSubModuleId?: string) {
    setSelectedSuperModule(module);
    const subModules = subModulesForModule[module.id] ?? (await fetchSubModulesForModule(module));
    const preferredSubModule =
      preferredSubModuleId && subModules.find(subModule => subModule.id === preferredSubModuleId);
    setSelectedSubModule(preferredSubModule || subModules[0] || null);
  }

  useEffect(() => {
    if (!modules.length || selectedSuperModule) return;

    const savedSuperModuleId = JSON.parse(localStorage.getItem(SUPER_MODULE_PREFS_KEY) || 'null');
    const savedSubModuleId = JSON.parse(localStorage.getItem(SUB_MODULE_PREFS_KEY) || 'null');

    const superModuleToSelect = (savedSuperModuleId && modules.find(m => m.id === savedSuperModuleId)) || modules[0];

    selectSuperModule(superModuleToSelect, savedSubModuleId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modules]);

  useEffect(() => {
    if (selectedSubModule) {
      setSelection({});
    }
  }, [selectedSubModule]);

  function onSelectionChange(nodeId: string, rowId: string) {
    const selectableNodesById = keyBy(flattenNodes(filteredNodesWithMaterial), 'id');

    if (searchValue && nodeId === rowId && selectableNodesById[nodeId]) {
      selectableNodesById[nodeId] = {
        ...selectableNodesById[nodeId],
        material: selectableNodesById[nodeId].material?.filter(material => fuzzyMatch(material.name, searchValue)),
      };
    }

    const newSelection = updateNodeTreeSelection(
      nodeId,
      rowId,
      selection,
      allNodesWithMaterialById,
      selectableNodesById,
    );
    setSelection(newSelection);
  }

  function handleClose() {
    closeModal(id, true);
  }

  async function handleConfirmClick() {
    if (!currentModule || !selectedNode) return;

    const materialToCopy = Object.keys(selection).filter(key => !allNodesWithMaterialById[key]);
    if (materialToCopy.length) {
      try {
        await copyMaterial(currentModule.id, selectedNode.id, materialToCopy);

        onSubmitCallback();

        const subLocationId = currentModule?.isExerciseModule
          ? ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_EXERCISE_MODULE_MATERIAL
          : ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_MATERIAL;

        capture({
          objectId: id,
          eventType: ANALYTICS_EVENT_TYPES.USER_MATERIAL_COPIED,
          subLocationId,
        });

        if (selectedSuperModule) localStorage.setItem(SUPER_MODULE_PREFS_KEY, JSON.stringify(selectedSuperModule.id));
        if (selectedSubModule) localStorage.setItem(SUB_MODULE_PREFS_KEY, JSON.stringify(selectedSubModule.id));

        handleClose();
      } catch (error) {
        setShowAlert('retry');
      }
    } else {
      setShowAlert('no-selection');
    }
  }

  function renderAlert() {
    if (showAlert === 'no-selection') {
      return <Alert prominence="warning">{t(`${BASE}.ALERTS.NO_SELECTION`)}</Alert>;
    }
    if (showAlert === 'retry') {
      return (
        <Alert
          prominence="danger"
          actions={{
            'aria-label': undefined,
            role: undefined,
            children: (
              <Button prominence="danger" onClick={handleConfirmClick}>
                {t(`${BASE}.ALERTS.RETRY.ACTION`)}
              </Button>
            ),
          }}
        >
          {t(`${BASE}.ALERTS.RETRY.TEXT`)}
        </Alert>
      );
    }

    return null;
  }

  function renderBody() {
    if (isLoading) {
      return <LoadingState variant="list" aria-label={t(`${BASE}.LOADING.A11Y_LABEL`)} />;
    }

    if (filteredNodesWithMaterial.length === 0 || !selectedSuperModule) {
      return (
        <GeneralNotice
          text={t(`${BASE}.EMPTY.TITLE`)}
          subText={searchValue ? t(`${BASE}.EMPTY.TEXT_SEARCH`) : t(`${BASE}.EMPTY.TEXT`)}
        />
      );
    }

    return (
      <NodeTree
        nodes={filteredNodesWithMaterial}
        onSelectionChange={onSelectionChange}
        selection={selection}
        selectedSuperModule={selectedSuperModule}
      />
    );
  }

  return (
    <Dialog
      id={id}
      modal
      className="copy-material-modal"
      onClose={handleClose}
      size="sm"
      aria-label={t(`${BASE}.A11Y_LABEL`)}
    >
      <Dialog.Header
        title={t(`${BASE}.TITLE`)}
        onClose={handleClose}
        aria-label={t('ACTIONS.CLOSE')}
        filters={
          modules.length ? (
            <>
              <ModuleSelectionField
                key="super-module-filter"
                modules={modules}
                selectedModule={selectedSuperModule}
                setSelectedModule={selectSuperModule}
                ariaLabel={t(`${BASE}.MODULE_FILTER.A11Y_LABEL`)}
                dropdownMenuAriaLabel={t(`${BASE}.MODULE_FILTER.DROPDOWN_MENU.A11Y_LABEL`)}
                useCatalogName
                size="md"
              />
              <ModuleSelectionField
                key="sub-module-filter"
                modules={(selectedSuperModule && subModulesForModule[selectedSuperModule.id]) || []}
                selectedModule={selectedSubModule}
                setSelectedModule={module => {
                  setSelectedSubModule(module);
                }}
                ariaLabel={t(`${BASE}.SUB_MODULE_FILTER.A11Y_LABEL`)}
                dropdownMenuAriaLabel={t(`${BASE}.SUB_MODULE_FILTER.DROPDOWN_MENU.A11Y_LABEL`)}
                size="md"
              />
            </>
          ) : undefined
        }
      >
        <SearchField
          value={searchValue}
          placeholder={t(`${BASE}.SEARCH.PLACEHOLDER`)}
          clearActionLabel={t(`${BASE}.SEARCH.CLEAR_LABEL`)}
          searchActionLabel={t(`${BASE}.SEARCH.SEARCH_LABEL`)}
          onChange={e => setSearchValue(e.target.value)}
          onSearch={handleSearch}
          onClear={() => {
            setSearchValue('');
            setFilteredNodesWithMaterial(nodesWithMaterial);
          }}
        />
      </Dialog.Header>
      <Dialog.Body
        alertGroupProps={showAlert ? { 'aria-label': undefined, role: undefined, children: renderAlert()! } : undefined}
      >
        {renderBody()}
      </Dialog.Body>
      <Dialog.Footer
        buttonGroupPropsRight={{
          role: 'group',
          'aria-label': undefined,
          children: (
            <>
              <Button prominence="neutral" onClick={handleClose} key="cancel">
                {t('ACTIONS.CANCEL')}
              </Button>
              <Button prominence="success" onClick={handleConfirmClick} key="apply">
                {t(`${BASE}.CONFIRM`)}
              </Button>
            </>
          ),
        }}
      />
    </Dialog>
  );
}
